import { useLayoutEffect, useRef, useState } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { ChevronDown } from 'lucide-react';
import { dataTypeLabels, leadStatuses, statusLabels, type Lead, type LeadFilters, type LeadStatus } from '../lib/types';
import { StatusBadge } from './StatusBadge';

export type LeadTableColumn = 'company' | 'location' | 'industry' | 'mailBackend' | 'dataType' | 'source' | 'uploaded' | 'campaign' | 'sentDate' | 'suppression';

interface LeadTableProps {
  leads: Lead[];
  selectedIds: Set<string>;
  allMatchedSelected: boolean;
  loading: boolean;
  visibleColumns: Set<LeadTableColumn>;
  sortBy?: LeadFilters['sortBy'];
  sortDir?: LeadFilters['sortDir'];
  density: 'comfortable' | 'compact';
  maskSensitive: boolean;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
  onStatusChange: (id: string, status: LeadStatus) => void;
  onOpenLead: (lead: Lead) => void;
  onSort: (sortBy: NonNullable<LeadFilters['sortBy']>) => void;
}

function displayName(lead: Lead): string {
  return lead.fullName || [lead.firstName, lead.lastName].filter(Boolean).join(' ') || 'No name';
}

function formatDate(value: string | null | undefined, fallback = '-'): string {
  if (!value) return fallback;
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: '2-digit', year: 'numeric' }).format(new Date(value));
}

function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!domain) return email;
  const visible = local.slice(0, Math.min(2, local.length));
  return `${visible}${'*'.repeat(Math.max(3, local.length - visible.length))}@${domain}`;
}

function enrichmentMeta(lead: Lead): Record<string, unknown> {
  const enrichment = lead.customFields?.enrichment;
  return enrichment && typeof enrichment === 'object' && !Array.isArray(enrichment) ? enrichment as Record<string, unknown> : {};
}

function mailBackendLabel(lead: Lead) {
  const enrichment = enrichmentMeta(lead);
  const provider = typeof enrichment.mxProvider === 'string' ? enrichment.mxProvider : '';
  const providerDomain = typeof enrichment.mxProviderDomain === 'string' ? enrichment.mxProviderDomain : '';
  const mailDomain = typeof enrichment.mailDomain === 'string' ? enrichment.mailDomain : lead.email.split('@')[1] ?? '';
  return {
    provider: provider || 'Not checked',
    detail: providerDomain || mailDomain || 'MX unavailable'
  };
}

export function LeadTable({
  leads,
  selectedIds,
  allMatchedSelected,
  loading,
  visibleColumns,
  sortBy,
  sortDir,
  density,
  maskSensitive,
  onToggle,
  onToggleAll,
  onStatusChange,
  onOpenLead,
  onSort
}: LeadTableProps) {
  const [statusMenuLeadId, setStatusMenuLeadId] = useState<string | null>(null);
  const allSelected = allMatchedSelected || (leads.length > 0 && leads.every((lead) => selectedIds.has(lead.id)));
  const columnCount = 4 + visibleColumns.size;
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);
  const virtual = leads.length > 200;
  useLayoutEffect(() => {
    const update = () => setScrollMargin((bodyRef.current?.getBoundingClientRect().top ?? 0) + window.scrollY);
    update();
    window.addEventListener('resize', update);
    const observer = new ResizeObserver(update);
    observer.observe(document.body);
    return () => { window.removeEventListener('resize', update); observer.disconnect(); };
  }, [leads.length, density, visibleColumns]);
  const virtualizer = useWindowVirtualizer({
    count: leads.length,
    estimateSize: () => density === 'compact' ? 88 : 110,
    getItemKey: index => leads[index].id,
    overscan: 12,
    scrollMargin,
    enabled: virtual
  });
  const items = virtualizer.getVirtualItems();
  const renderedRows = virtual ? items.map(item => ({ lead: leads[item.index], index: item.index })) : leads.map((lead, index) => ({ lead, index }));
  const topPadding = virtual && items.length ? Math.max(0, items[0].start - scrollMargin) : 0;
  const bottomPadding = virtual && items.length ? Math.max(0, virtualizer.getTotalSize() - (items[items.length - 1].end - scrollMargin)) : 0;
  const sortableHeader = (label: string, key: NonNullable<LeadFilters['sortBy']>) => (
    <button
      className={`table-sort-button ${sortBy === key ? 'active' : ''}`}
      type="button"
      onClick={() => onSort(key)}
      title={`Sort by ${label}`}
    >
      <span>{label}</span>
      {sortBy === key && <small>{sortDir === 'desc' ? 'DESC' : 'ASC'}</small>}
    </button>
  );

  return (
    <div className={`table-shell table-density-${density}`}>
      <table className="lead-table">
        <thead>
          <tr>
            <th className="select-cell"><input aria-label="Select all rows" type="checkbox" checked={allSelected} onChange={onToggleAll} /></th>
            <th>{sortableHeader('Lead', 'lead')}</th>
            {visibleColumns.has('company') && <th>{sortableHeader('Company', 'company')}</th>}
            {visibleColumns.has('location') && <th>{sortableHeader('Location', 'location')}</th>}
            {visibleColumns.has('industry') && <th>{sortableHeader('Industry', 'industry')}</th>}
            {visibleColumns.has('mailBackend') && <th>Mail backend</th>}
            <th>Website updated</th>
            {visibleColumns.has('dataType') && <th>{sortableHeader('Data type', 'dataType')}</th>}
            {visibleColumns.has('source') && <th>{sortableHeader('Source', 'source')}</th>}
            {visibleColumns.has('uploaded') && <th>{sortableHeader('Uploaded', 'uploaded')}</th>}
            <th>{sortableHeader('Status', 'status')}</th>
            {visibleColumns.has('campaign') && <th>Campaign</th>}
            {visibleColumns.has('sentDate') && <th>{sortableHeader('Sent date', 'sentDate')}</th>}
            {visibleColumns.has('suppression') && <th>Suppression</th>}
          </tr>
        </thead>
        <tbody ref={bodyRef}>
          {topPadding > 0 && <tr aria-hidden="true"><td colSpan={columnCount} style={{ height: topPadding, padding: 0, border: 0 }} /></tr>}
          {loading ? (
            Array.from({ length: 8 }).map((_, index) => <tr className="skeleton-row" key={index}><td colSpan={columnCount}><span /></td></tr>)
          ) : leads.length === 0 ? (
            <tr><td className="empty-state" colSpan={columnCount}>No leads match the current filters.</td></tr>
          ) : renderedRows.map(({ lead, index }) => {
            const name = displayName(lead);
            const hasName = name !== 'No name';
            const emailText = maskSensitive ? maskEmail(lead.email) : lead.email;
            const mailBackend = mailBackendLabel(lead);
            return (
            <tr key={lead.id} data-index={index} ref={virtual ? virtualizer.measureElement : undefined}>
              <td className="select-cell"><input aria-label={`Select ${lead.email}`} type="checkbox" checked={allMatchedSelected || selectedIds.has(lead.id)} onChange={() => onToggle(lead.id)} /></td>
              <td><button className={`lead-identity ${hasName ? '' : 'email-primary'}`} type="button" onClick={() => onOpenLead(lead)}><strong>{hasName ? name : emailText}</strong><span>{hasName ? emailText : 'No saved name'}</span></button></td>
              {visibleColumns.has('company') && <td><div className="stacked-cell"><strong>{lead.company || '-'}</strong><span>{lead.title || 'No designation'}</span></div></td>}
              {visibleColumns.has('location') && <td><div className="stacked-cell"><strong>{[lead.city, lead.state].filter(Boolean).join(', ') || '-'}</strong><span>{lead.country || 'Country unavailable'}</span></div></td>}
              {visibleColumns.has('industry') && <td><span className="data-type-cell">{lead.industry || 'Not set'}</span></td>}
              {visibleColumns.has('mailBackend') && <td><div className="stacked-cell"><strong>{mailBackend.provider}</strong><span>{mailBackend.detail}</span></div></td>}
              <td>{(() => {
                const evidence = enrichmentMeta(lead).websiteModified as { date?: string; source?: string; url?: string } | null;
                return evidence?.date ? <span title={`${evidence.source ?? 'Publisher metadata'} | ${evidence.url ?? ''}`}>{evidence.date}<small style={{ display: 'block' }}>Publisher reported</small></span> : <span>Unknown</span>;
              })()}</td>
              {visibleColumns.has('dataType') && <td><span className="data-type-cell">{dataTypeLabels[lead.dataType] ?? 'Other'}</span></td>}
              {visibleColumns.has('source') && <td><div className="stacked-cell source-cell"><strong>{lead.sourceFile || 'Manual entry'}</strong><span>{lead.sourceSheet || 'Workspace'}</span></div></td>}
              {visibleColumns.has('uploaded') && <td>{formatDate(lead.uploadDate)}</td>}
              <td>
                <div className="status-menu-wrap">
                  <button className="status-control" type="button" aria-expanded={statusMenuLeadId === lead.id} onClick={() => setStatusMenuLeadId((current) => current === lead.id ? null : lead.id)}><StatusBadge status={lead.status} suppressed={Boolean(lead.suppressedAt)} /><ChevronDown size={14} /></button>
                  {statusMenuLeadId === lead.id && <div className="status-menu" role="menu">{leadStatuses.map((status) => <button key={status} type="button" role="menuitem" className={status === lead.status ? 'selected' : ''} onClick={() => { onStatusChange(lead.id, status); setStatusMenuLeadId(null); }}>{statusLabels[status]}</button>)}</div>}
                </div>
              </td>
              {visibleColumns.has('campaign') && <td>{lead.campaignId || 'Unassigned'}</td>}
              {visibleColumns.has('sentDate') && <td>{formatDate(lead.sentDate, 'Not sent')}</td>}
              {visibleColumns.has('suppression') && <td>{lead.suppressedAt ? <span className="suppressed-pill">{lead.suppressionReason ? statusLabels[lead.suppressionReason] : 'Suppressed'}</span> : <span className="available-pill">Available</span>}</td>}
            </tr>
            );
          })}
          {bottomPadding > 0 && <tr aria-hidden="true"><td colSpan={columnCount} style={{ height: bottomPadding, padding: 0, border: 0 }} /></tr>}
        </tbody>
      </table>
    </div>
  );
}
